import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type {
  IconColor,
  IconName,
} from '@mylighthouse/prism-ember/types/components/prism/icon';
import { t } from 'ember-intl';
import { eq, isEmpty, not } from 'ember-truth-helpers';
import type { MarketDemandLevel } from 'lumen/utils/constants/general-constants';
import { MARKET_DEMAND_LEVELS_CLASSES } from 'lumen/utils/constants/general-constants';

interface GeneralSmartInsightsDetailViewSmartInsightKpiBlockSignature {
  Args: {
    demandLevel?: MarketDemandLevel | null;
    isChangeValueHidden?: boolean;
    label: string;
    paceValue?: number;
    paceValueFormatted?: string;
    value: string;
  } & (
    | {
        changeValue?: number;
        changeValueFormatted?: string;
      }
    | {
        changeValue: never;
        changeValueFormatted: never;
      }
  );
}

export default class GeneralSmartInsightsDetailViewSmartInsightKpiBlock extends Component<GeneralSmartInsightsDetailViewSmartInsightKpiBlockSignature> {
  get changeValueColorClass(): string {
    const { changeValue } = this.args;

    if (!changeValue || changeValue === 0) {
      return 'text-neutral-subdued';
    }

    return changeValue > 0 ? 'text-success-default' : 'text-critical-default';
  }

  get demandLevelClass(): string {
    const { demandLevel } = this.args;

    if (!demandLevel) {
      return '';
    }

    return MARKET_DEMAND_LEVELS_CLASSES[demandLevel];
  }

  get isChangeValueAvailable(): boolean {
    return !isEmpty(this.args.changeValue);
  }

  get isPaceValueAvailable(): boolean {
    return !isEmpty(this.args.paceValue);
  }

  get paceIconColor(): IconColor {
    const { paceValue } = this.args;

    if (!paceValue) {
      return 'default';
    }

    return paceValue >= 0 ? 'on-success' : 'on-critical';
  }

  get paceIconName(): IconName {
    const { paceValue } = this.args;

    if (!paceValue) {
      return 'trending-up';
    }

    return paceValue >= 0 ? 'trending-up' : 'trending-down';
  }

  get paceValueColorClass(): string {
    const { paceValue } = this.args;

    if (!paceValue || paceValue === 0) {
      return 'text-neutral-subdued';
    }

    return paceValue > 0 ? 'text-success-default' : 'text-critical-default';
  }

  <template>
    <div class="flex flex-row gap-400">
      <div class="flex flex-1 flex-col">
        <div class="flex flex-row items-center gap-100">
          {{#if this.demandLevelClass}}
            <div class="identifier-square {{this.demandLevelClass}}"></div>
          {{/if}}
          <div
            class="text-400--bold"
            data-test-id="smart-insight-kpi-block-value"
          >{{@value}}</div>

          {{#if this.isChangeValueAvailable}}
            <span
              class="{{this.changeValueColorClass}} text-200--bold"
            >{{@changeValueFormatted}}</span>
          {{else if (not @isChangeValueHidden)}}
            <span class="text-200--bold text-neutral-subdued">
              {{t "general.missing"}}
            </span>
          {{/if}}
        </div>

        <div
          class="text-200--regular text-neutral-subdued"
          data-test-id="smart-insight-kpi-block-label"
        >
          {{@label}}
        </div>
      </div>

      {{#if this.isPaceValueAvailable}}
        <div
          class="flex flex-col items-end"
          data-test-id="smart-insight-kpi-block-pace"
        >
          <div
            class="{{this.paceValueColorClass}}
              text-200--bold flex flex-row items-center gap-100"
          >
            {{#unless (eq @paceValue 0)}}
              <PrismIcon
                @color={{this.paceIconColor}}
                @name={{this.paceIconName}}
                @size="small"
              />
            {{/unless}}

            <span>{{@paceValueFormatted}}</span>
          </div>

          <div class="text-200--regular text-neutral-subdued">
            {{t "smart_insights.detail_view.pace_vs_stly"}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
